// Live data without refresh: a page names the scopes it shows, and refetches when one moves.
// One poller per tab batches every mounted page's scopes into GET /api/sync/rev.
// Scopes, the echo rule and how to add a surface: docs/features/live-data.md.

import { useCallback, useEffect, useRef } from 'react';
import { apiFetchAuthed } from '../apiHelpers';

/** The scope names the triggers in api/migrations/159.do.live-sync.sql write. */
export const liveScope = {
  spark: 'spark',
  sparkProject: (id: string) => `spark:project:${id}`,
  messages: (entityType: string, entityId: string | number) => `messages:${entityType}:${entityId}`,
};

export interface LiveContext {
  /**
   * True once a hold has begun on these scopes since this change fired: the refetch may have
   * read the database before the held write landed. Check it before applying the response;
   * a stale answer is dropped and the change fires again once the hold ends.
   */
  stale: () => boolean;
}

/** Return the refetch's promise: if it rejects, the change fires again after the next good poll. */
export type LiveChange = (changed: string[], ctx: LiveContext) => void | Promise<unknown>;

interface Subscriber {
  scopes: string[];
  onChange: { current: LiveChange };
  intervalMs: number;
  pending: Set<string>;
  since: number;
  /** Its last refetch has not settled (its number, 0 when idle). A change meanwhile waits, so answers land in order. */
  busy: number;
}

const MAX_PER_REQUEST = 20; // the API's cap (api/src/sync/sync-router.js)
const MAX_BACKOFF_MS = 5 * 60 * 1000;
const LATE_BASELINE_MS = 1000;
// A refetch that never answers (a dead socket; apiFetchAuthed has no timeout) stops holding the
// page's next change after this long, or that page would never update again.
const STUCK_REFETCH_MS = 30 * 1000;
let refetchSeq = 0;

const subscribers = new Set<Subscriber>();
// Last rev seen per scope. Kept after unmount: a page that comes back compares against the
// rev from before its own load, so a change in between fires rather than hides.
const seen = new Map<string, number>();
const holds = new Map<string, number>();
const holdEpochs = new Map<string, number>();
let timer: ReturnType<typeof setTimeout> | undefined;
let inFlight = false;
let again = false;
let failures = 0;

const watched = () => Array.from(new Set(Array.from(subscribers).flatMap((s) => s.scopes)));
const isHeld = (sub: Subscriber) => sub.scopes.some((s) => holds.has(s));
const epochOf = (sub: Subscriber) => sub.scopes.reduce((n, s) => n + (holdEpochs.get(s) || 0), 0);

function fire(sub: Subscriber, changed: string[]) {
  if (!subscribers.has(sub)) return;
  if (sub.busy) { changed.forEach((s) => sub.pending.add(s)); return; }
  const epoch = epochOf(sub);
  let dropped = false;
  const stale = () => {
    if (!isHeld(sub) && epochOf(sub) === epoch) return false;
    if (!dropped) {
      dropped = true;
      changed.forEach((s) => sub.pending.add(s));
      // The hold may already be over (it began and ended during the refetch): fire again now.
      if (!isHeld(sub)) setTimeout(() => flush(sub), 0);
    }
    return true;
  };
  let result: unknown;
  try {
    result = sub.onChange.current(changed, { stale });
  } catch (err) {
    // One page's bug must not stop the others' updates or read as an API failure; rethrown so it still surfaces.
    setTimeout(() => { throw err; }, 0);
    return;
  }
  if (!result || typeof (result as PromiseLike<unknown>).then !== 'function') return;
  const mine = ++refetchSeq;
  sub.busy = mine;
  const settle = () => {
    if (sub.busy !== mine) return false;
    sub.busy = 0;
    window.clearTimeout(stuck);
    return true;
  };
  const stuck = window.setTimeout(() => { if (settle()) flush(sub); }, STUCK_REFETCH_MS);
  Promise.resolve(result).then(
    // Whatever moved while it was out fires now, rather than racing it.
    () => { if (settle()) flush(sub); },
    // A failure waits for the next good poll, as before; firing at once would hammer a failing API.
    () => { settle(); changed.forEach((s) => sub.pending.add(s)); },
  );
}

function flush(sub: Subscriber) {
  if (!sub.pending.size || isHeld(sub)) return;
  const changed = Array.from(sub.pending);
  sub.pending.clear();
  fire(sub, changed);
}

/** The revs, and when the last request went out: the latest moment any of them was read. */
async function fetchRevs(scopes: string[]): Promise<{ revs: Record<string, number>; sentAt: number }> {
  const revs: Record<string, number> = {};
  let sentAt = Date.now();
  for (let i = 0; i < scopes.length; i += MAX_PER_REQUEST) {
    const chunk = scopes.slice(i, i + MAX_PER_REQUEST).map(encodeURIComponent).join(',');
    sentAt = Date.now();
    // eslint-disable-next-line no-await-in-loop
    const res = await apiFetchAuthed(`/sync/rev?scopes=${chunk}`, { silent: true });
    Object.assign(revs, res?.revs || {});
  }
  return { revs, sentAt };
}

/** Record what moved, then fire every page with something pending: new moves, and refetches that failed. */
function apply({ revs, sentAt }: { revs: Record<string, number>; sentAt: number }) {
  const moved = new Set<string>();
  const first = new Set<string>();
  Object.entries(revs).forEach(([scope, rev]) => {
    const before = seen.get(scope);
    seen.set(scope, rev);
    if (before === undefined) first.add(scope);
    else if (before !== rev) moved.add(scope);
  });
  subscribers.forEach((sub) => {
    // A first look taken long after the page loaded (a tab opened in the background, a failed
    // first poll) cannot tell what changed in between, so that page refetches once.
    const late = sentAt - sub.since > LATE_BASELINE_MS;
    sub.scopes.forEach((s) => { if (moved.has(s) || (late && first.has(s))) sub.pending.add(s); });
    flush(sub);
  });
}

function nextDelay() {
  const base = Math.min(...Array.from(subscribers).map((s) => s.intervalMs));
  return failures ? Math.min(base * 2 ** failures, MAX_BACKOFF_MS) : base;
}

function schedule(delay = nextDelay()) {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  if (inFlight || !subscribers.size || document.hidden) return;
  timer = setTimeout(tick, delay);
}

async function tick() {
  timer = undefined;
  if (!subscribers.size || document.hidden) return;
  inFlight = true;
  again = false;
  try {
    apply(await fetchRevs(watched()));
    failures = 0;
  } catch {
    failures += 1;
  } finally {
    inFlight = false;
  }
  schedule(again ? 0 : undefined);
}

/** Check soon: a new scope wants its baseline close to the page's own load. */
function checkSoon() {
  if (inFlight) again = true;
  else schedule(0);
}

function onVisibility() {
  if (document.hidden) schedule();
  else checkSoon();
}

function subscribe(sub: Subscriber) {
  if (!subscribers.size) document.addEventListener('visibilitychange', onVisibility);
  subscribers.add(sub);
  if (sub.scopes.some((s) => !seen.has(s))) checkSoon();
  else if (timer === undefined) schedule();
}

function unsubscribe(sub: Subscriber) {
  subscribers.delete(sub);
  if (subscribers.size) return;
  document.removeEventListener('visibilitychange', onVisibility);
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
}

/**
 * Hold scopes while your own write is in flight, so no refetch lands over the value you just
 * set optimistically. A change that arrives during the hold fires once it ends.
 */
export function holdScopes<T>(scopes: string[], work: Promise<T>): Promise<T> {
  scopes.forEach((s) => {
    holds.set(s, (holds.get(s) || 0) + 1);
    holdEpochs.set(s, (holdEpochs.get(s) || 0) + 1);
  });
  const release = () => {
    scopes.forEach((s) => {
      const left = (holds.get(s) || 1) - 1;
      if (left) holds.set(s, left);
      else holds.delete(s);
    });
    subscribers.forEach(flush);
  };
  return Promise.resolve(work).then(
    (value) => { release(); return value; },
    (err) => { release(); throw err; },
  );
}

/**
 * Keep a page current: `onChange` runs with the scopes that moved since the last look. The
 * first look at a scope only records where it stands, unless it came more than a second after
 * the page subscribed; then it fires once. Polls every `intervalMs` while the tab is visible,
 * checks at once when it comes back, and backs off while the API is failing. Return the
 * refetch's promise from `onChange`: a rejection fires the same scopes again after the next
 * good poll.
 */
export function useLiveScopes(scopes: string[], onChange: LiveChange, { intervalMs = 8000 }: { intervalMs?: number } = {}) {
  const callback = useRef(onChange);
  callback.current = onChange;
  const key = scopes.filter(Boolean).join('|');

  useEffect(() => {
    if (!key) return undefined;
    const sub: Subscriber = { scopes: key.split('|'), onChange: callback, intervalMs, pending: new Set(), since: Date.now(), busy: 0 };
    subscribe(sub);
    return () => unsubscribe(sub);
  }, [key, intervalMs]);

  const holdWhile = useCallback(<T>(work: Promise<T>) => holdScopes(key ? key.split('|') : [], work), [key]);
  return { holdWhile };
}

/** Tests only: forget every subscriber, rev and hold. */
export function resetLiveScopesForTests() {
  subscribers.clear();
  seen.clear();
  holds.clear();
  holdEpochs.clear();
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
  inFlight = false;
  again = false;
  failures = 0;
  document.removeEventListener('visibilitychange', onVisibility);
}
